////////////////////////////////////////////////////////
//                       Nodes                        //
////////////////////////////////////////////////////////

import { useNodeCanvasStore } from '../../store';
import { Service } from '../storeClasses/service';
import { Topic } from '../storeClasses/topic';
import { Node } from '../storeClasses/node';
import {
  addDeleteButton,
  addEditButton,
  getCanvasCurrentPackage
} from './drawCanvas';
import { V4UUID } from '@/firebase/database';
import { useGlobalStore } from '@/modules/globalModule/store';
import { Position } from '../storeClasses/canvasObjects';
import { addNewTopic } from './drawTopic';
import { onServiceArcPressup } from './drawService';

/**
 * Draws a node onto the stage.
 * @param {*} stage
 * @param {*} node - node object as stored in store
 * @param {*} editing - whether or not to display edit buttons initially
 */
function drawNode(stage: any, node: Node, editing: boolean = false) {
  // creating container for node objects
  const container: any = new createjs.Container();
  container.name = 'n' + node.id;
  container.x = node.position.x;
  container.y = node.position.y;
  container.objId = node.id;

  // creating node graphical circle
  const circle = new createjs.Shape();
  circle.graphics
    .beginLinearGradientFill(['#5CC8F9', '#0B82C3'], [0, 1], 0, 0, 100, 0)
    .drawCircle(0, 0, node.radius);
  circle.name = 'nc' + node.id;
  container.addChild(circle);

  // Adding name to node
  const nameText = new createjs.Text(node.name, '20px Arial', '#000000');
  nameText.x = -nameText.getMeasuredWidth() / 2;
  nameText.y = -node.radius / 2;
  nameText.name = 'nt' + node.id;
  container.addChild(nameText);
  let mousedownCount = 0;
  // movement functionality
  circle.on('mousedown', function (evt: any) {
    const ncStore = useNodeCanvasStore();
    ncStore.currDragOffset = {
      x: evt.stageX - container.x,
      y: evt.stageY - container.y
    };
    // If currDragOffset is identical to the drag offset in 300 ms, then we record a hold down that has taken that amount of time
    // We know that they have no pressed up if mousedownCount is not equal to 0 as mousedownCount is reset on pressup
    mousedownCount++;
    setTimeout(() => {
      const cmp1 = JSON.stringify(ncStore.currDragOffset);
      const cmp2 = JSON.stringify({
        x: evt.stageX - container.x,
        y: evt.stageY - container.y
      });
      if (cmp1 == cmp2 && mousedownCount != 0) {
        circle.dispatchEvent('dblclick');
      }
      mousedownCount = 0;
    }, 400);
  });
  circle.on('pressmove', function (evt: any) {
    let ncStore = useNodeCanvasStore();
    ncStore.dragObjId = node.id;
    container.x = evt.stageX - ncStore.currDragOffset.x;
    container.y = evt.stageY - ncStore.currDragOffset.y;
    ncStore.moveObj(node.id, container.x, container.y);
  });
  circle.on('pressup', () => {
    mousedownCount = 0;
    const ncStore = useNodeCanvasStore();
    ncStore.dragObjId = 'undefined';
  });
  // edit node funcitonality
  circle.on('dblclick', function () {
    const ncStore = useNodeCanvasStore();
    ncStore.editingNodeId = node.id;
  });

  // functionality once editing node finished
  stage.on('editNodeEnd', function () {
    nameText.text = node.name;
    nameText.x = -nameText.getMeasuredWidth() / 2; // centering name
  });

  // editing buttons
  const delBtn = addDeleteButton(
    stage,
    container,
    node,
    node.radius * Math.sin(Math.PI * 1.25) - 16,
    node.radius * Math.cos(Math.PI * 1.25) - 16
  );
  const editBtn = addEditButton(
    stage,
    container,
    node,
    node.radius * Math.sin(Math.PI * 0.75) - 17,
    node.radius * Math.cos(Math.PI * 0.75) - 17,
    'n'
  );
  editBtn.visible = delBtn.visible = editing;
  circle.shadow = new createjs.Shadow('#7F7F7F', 5, 5, 1);
  stage.addChild(container);
  stage.update();
  console.log(container);
}

export function removeNode(stage: any, id: V4UUID) {
  console.log(id);
  useGlobalStore().deleteElemById(id);
  const toRemove = stage.children.find((ea: any) => {
    console.log(ea.objId);
    return ea.objId === id;
  });
  stage.removeChild(toRemove);
}

/**
 * Adds all nodes in store to the canvas
 * @param {*} stage
 */
export function addNodes(stage: any) {
  const pkg = getCanvasCurrentPackage();
  if (!pkg) return;
  pkg.nodes.forEach((node) => {
    drawNode(stage, node);
  });
}

/**
 * Creates a new node and draws it onto the stage
 * @param {*} stage
 * @param {*} x
 * @param {*} y
 */
export function addNewNode(stage: any, x: number, y: number) {
  const pkg = getCanvasCurrentPackage();
  const globalStore = useGlobalStore();
  if (!pkg) {
    console.warn('addNewNode: current package not found');
    return;
  }
  const newNode = globalStore.createNodeInPkg(pkg.id, {
    x: x,
    y: y,
    name: `Node ${pkg.nodes.length + 1}`
  });
  console.log(newNode);
  drawNode(stage, newNode, true);
}

export function onNodeArcPressup(
  node: Node,
  other: Service | Topic | Node,
  options: any
): void {
  const globalStore = useGlobalStore();
  const elemType = globalStore.getTypeOfElemById(other.id);
  if (elemType === 'Topic') {
    const connectData = {
      topic: other.id,
      node: node.id
    };
    useNodeCanvasStore().connectingTopicNode = connectData;
  } else if (other instanceof Service) {
    if (options.objMatch?.type === 'server') {
      other.addServer(node);
    } else if (options.objMatch?.type === 'client') {
      other.addClient(node);
    } else {
      onServiceArcPressup(other, node, options);
    }
  } else if (other instanceof Node) {
    if (other.id == node.id) {
      return;
    }
    const center: Position = {
      x: Math.abs(other.position.x - node.position.x) / 2,
      y: Math.abs(other.position.y - node.position.y) / 2
    };
    const topic = addNewTopic(options.stage, center.x, center.y);
    if (!topic) {
      console.warn('node onArcPressup: topic failed to create');
      return;
    }
    const connectData = {
      topic: topic.id,
      node: node.id
    };
    const ncStore = useNodeCanvasStore();
    ncStore.connectingTopicNode = connectData;
    function checkFlag() {
      if (
        ncStore.connectingTopicNode !== undefined ||
        ncStore.connectingTopicNode !== undefined ||
        !topic
      ) {
        window.setTimeout(
          checkFlag,
          100
        ); /* this checks the flag every 100 milliseconds*/
      } else {
        ncStore.connectingTopicNode = { topic: topic.id, node: other.id };
      }
    }
    checkFlag();
  }
}
